/**
 * Versioned dashboard-config DTO — the on-disk shape produced by
 * `exportConfig` and consumed by `importConfig`.
 *
 * Everything that makes a dashboard look the way it does lives here
 * EXCEPT:
 *  - the raw dataset (datasets stay local; imports bind to whatever
 *    the user has loaded in the current session),
 *  - active filter chips (ephemeral session state),
 *  - the active datasetId (gets remapped on import).
 *
 * `version` is the single source of truth for schema migrations.
 * Incrementing it without a migration step is a breaking change.
 *
 * v1 → v2 (D-020 follow-up): widgets / pinned filters / layouts are
 * now keyed per dashboard mode (normal vs compare), so the export
 * bundles BOTH modes' slices together. v1 configs (single implicit
 * mode) are accepted on import and migrated by stuffing them into
 * the v2 `modes.normal` slice with an empty compare slice. Compare
 * configuration (date column + granularity + period A/B) is also
 * carried in the bundle so a re-import restores the full state.
 */
import type { WidgetConfig } from './widget';
import type { ColumnMeta, ColumnOverrides } from './dataset';
import type { ColumnTransform } from './query';
import type {
  CompareGranularity,
  ComparePeriod,
} from '@/persistence/config-db';

/** Current export format version. Bump + add a migration when shape changes. */
export const CONFIG_VERSION = 2 as const;

/** Pre-D-020 single-mode export shape, kept for inbound migration only. */
export const LEGACY_CONFIG_VERSION = 1 as const;

/** One react-grid-layout item, kept serialization-compatible with RGL v2. */
export type DashboardLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
};

/** Per-mode slice — widgets + pinned filters + RGL positions. */
export type DashboardModeSlice = {
  widgets: WidgetConfig[];
  /** Column names the user pinned to the filter bar, in display order. */
  pinnedFilters: string[];
  /**
   * RGL positions per zone. Missing keys are allowed — the consumer
   * falls back to `computeZoneLayout` auto-generation.
   */
  layouts: {
    kpi: DashboardLayoutItem[];
    chart: DashboardLayoutItem[];
  };
};

/**
 * Compare-mode configuration as serialized in the dashboard bundle.
 * Mirrors {@link CompareConfigEntry} minus the `datasetId` (the import
 * step rebinds to the active dataset) and `updatedAt` (regenerated on
 * persist).
 */
export type SerializedCompareConfig = {
  dateColumn: string;
  granularity: CompareGranularity;
  periodA: ComparePeriod;
  periodB: ComparePeriod;
};

export type DashboardConfig = {
  version: typeof CONFIG_VERSION;
  /** ISO timestamp produced with `new Date().toISOString()`. */
  exportedAt: string;
  /**
   * Column fingerprint the export was built against — NOT the data.
   * Used by the import step to diff against the current dataset and
   * propose mappings for missing columns.
   */
  sourceColumns: ColumnMeta[];
  /** Per-mode slices. `compare` may be empty when the user never used it. */
  modes: {
    normal: DashboardModeSlice;
    compare: DashboardModeSlice;
  };
  /** Column overrides apply to the dataset, not to a specific mode. */
  columnOverrides: ColumnOverrides;
  valueGroupings: Record<string, ColumnTransform>;
  /** Compare-mode period selection. `null` when the user hasn't set one. */
  compareConfig: SerializedCompareConfig | null;
};

/**
 * v1 (legacy) shape, accepted on import and migrated into a v2 bundle
 * with the legacy slice landing as `modes.normal` and an empty compare
 * side. Kept here so the type is reachable from the migration helper.
 */
export type LegacyDashboardConfigV1 = {
  version: typeof LEGACY_CONFIG_VERSION;
  exportedAt: string;
  sourceColumns: ColumnMeta[];
  widgets: WidgetConfig[];
  pinnedFilters: string[];
  columnOverrides: ColumnOverrides;
  valueGroupings: Record<string, ColumnTransform>;
  layouts: {
    kpi: DashboardLayoutItem[];
    chart: DashboardLayoutItem[];
  };
};
